import React, { useId, useMemo, useRef } from "react";
import { UIComponents } from "@/components/ui";
import { cn } from "@/shared/utils/utils";
import { showToastDynamic } from "@/shared/utils/toast.util";
import UploadIcon from "@/assets/images/upload-icon.svg?react";
import { FileDropzoneProps } from "./type";

const FileDropzone = (props: FileDropzoneProps): React.ReactElement => {
  const {
    acceptedExtensions,
    onFilesAccepted,
    helperText,
    multiple = false,
    title = "Drag & drop attachments here",
    browseLabel = "Browse Files",
    showBrowseControls = true,
    invalidFileMessage = "Selected file type is not allowed.",
    disabled = false,
    className,
    children,
  } = props;

  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const allowAllFiles = useMemo(
    () =>
      acceptedExtensions.length === 0 ||
      acceptedExtensions.some((extension) => extension.trim() === "*"),
    [acceptedExtensions],
  );
  const normalizedExtensions = useMemo(
    () =>
      acceptedExtensions.map((extension) =>
        extension.startsWith(".")
          ? extension.toLowerCase()
          : `.${extension.toLowerCase()}`,
      ),
    [acceptedExtensions],
  );

  const accept = allowAllFiles ? undefined : normalizedExtensions.join(",");
  const displayTitle = showBrowseControls ? `Or ${title}` : title;

  const selectFiles = (fileList: FileList | null): void => {
    if (!fileList || disabled) {
      return;
    }

    const validFiles = allowAllFiles
      ? Array.from(fileList)
      : Array.from(fileList).filter((file) =>
          normalizedExtensions.some((extension) =>
            file.name.toLowerCase().endsWith(extension),
          ),
        );

    if (validFiles.length === 0) {
      showToastDynamic(invalidFileMessage, "error");
      return;
    }

    if (validFiles.length !== fileList.length) {
      showToastDynamic(
        `Some selected files were skipped. ${invalidFileMessage}`,
        "error",
      );
    }

    onFilesAccepted(multiple ? validFiles : [validFiles[0]]);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    selectFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    selectFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleBrowseClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  return (
    <div className={cn("rounded-20 bg-white p-4", className)}>
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        aria-label="File upload"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={handleInputChange}
      />
      <div
        className={cn(
          "flex min-h-[250px] flex-col items-center justify-center rounded-20 border border-dashed border-primary px-5 py-12 text-center",
          disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer",
        )}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onKeyDown={handleKeyDown}
      >
        {showBrowseControls && (
          <>
            <UploadIcon className="mb-2" style={{ color: "var(--primary)" }} />
            <UIComponents.button
              type="button"
              variant="outline"
              className="mb-6 min-h-11 rounded-10 px-5 text-lg shadow-sm"
              onClick={handleBrowseClick}
            >
              {browseLabel}
            </UIComponents.button>
          </>
        )}
        {children ?? (
          <div className="space-y-2">
            <p className="text-2xl font-normal leading-normal text-foreground">
              {displayTitle}
            </p>
            <p className="text-lg leading-normal text-placeholder">
              {helperText}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDropzone;
